import { ActivityIndicator, ScrollView } from "react-native";
import { Stack } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import {
  NotificationPreferencesForm,
  NotificationPushUnavailableBanner,
  type NotificationChannel,
  type PushAvailability,
} from "@visioni/a3s-notifications/app";
import { useNotificationPreferencesPage } from "@src/features/settings/hooks";
import { useHasActivePushDevice } from "@src/features/notifications";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

/** Push feature flag off: no column to opt in with. */
const PUSH_HIDDEN: ReadonlySet<NotificationChannel> = new Set(["push"]);

export default function NotificationPreferencesScreen() {
  const { theme } = useUnistyles();
  const {
    isNotificationSettingsEnabled,
    isLoading,
    isError,
    allCameras,
    sites,
    cameras,
    onToggleAllCamerasChannel,
    onToggleSiteChannel,
    onToggleCameraChannel,
    phoneNumber,
    onSavePhoneNumber,
  } = useNotificationPreferencesPage();
  const isPushEnabled = useBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);
  // Web registers no device of its own, so Push is only worth ticking once one
  // of the user's phones is registered. A failed check with no earlier answer
  // leaves Push locked (a tick might never be delivered) and says the check
  // failed, rather than claiming there is no device. Loading shows nothing.
  const activeDevice = useHasActivePushDevice({ enabled: isNotificationSettingsEnabled && isPushEnabled });
  const pushAvailability: PushAvailability =
    activeDevice.data === true ? "available"
    : activeDevice.data === false || activeDevice.isError ? "unavailable"
    : "unknown";

  if (!isNotificationSettingsEnabled) {
    return null;
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <WebLayout>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
          {isLoading ?
            <View py="4xl" alignItems="center">
              <ActivityIndicator color={theme.colors.text as string} />
            </View>
          : isError ?
            <View py="4xl" alignItems="center">
              <Text textStyle="sm/regular" color="textMuted">
                {"Couldn't load your notification preferences. Please try again."}
              </Text>
            </View>
          : <NotificationPreferencesForm
              allCameras={allCameras}
              onToggleAllCamerasChannel={onToggleAllCamerasChannel}
              sites={sites}
              onToggleSiteChannel={onToggleSiteChannel}
              cameras={cameras}
              onToggleCameraChannel={onToggleCameraChannel}
              phoneNumber={phoneNumber}
              onSavePhoneNumber={onSavePhoneNumber}
              hiddenChannels={isPushEnabled ? undefined : PUSH_HIDDEN}
              pushAvailability={pushAvailability}
              pushNotice={
                !isPushEnabled ? null
                : activeDevice.data === false ?
                  <NotificationPushUnavailableBanner />
                : activeDevice.data === undefined && activeDevice.isError ?
                  <NotificationPushUnavailableBanner reason="checkFailed" />
                : null
              }
            />
          }
        </ScrollView>
      </WebLayout>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
  },
}));
